import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { documentsApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Loader2, CheckCircle, FileText, PenLine } from 'lucide-react';
import toast from 'react-hot-toast';

export default function PublicDocumentSign() {
  const { viewHash } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [doc, setDoc] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signedAt, setSignedAt] = useState(null);

  // Form fields
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Canvas
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await documentsApi.getByViewHash(viewHash);
        setDoc(res.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [viewHash]);

  // Canvas helpers

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  function startDraw(e) {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawing.current = true;
  }

  function draw(e) {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    setHasSignature(true);
  }

  function stopDraw(e) {
    e.preventDefault();
    isDrawing.current = false;
  }

  function clearSignature() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  }

  function getSignatureDataUrl() {
    return canvasRef.current?.toDataURL('image/png') ?? '';
  }

  // Submit

  async function handleSubmit(e) {
    e.preventDefault();
    if (!signerName || !signerEmail || !hasSignature || !confirmed) return;
    setSubmitting(true);
    try {
      await documentsApi.sign(viewHash, {
        signatureData: getSignatureDataUrl(),
        signerName,
        signerEmail,
      });
      setSignedAt(new Date());
      setSigned(true);
    } catch {
      toast.error('Fehler beim Unterzeichnen. Bitte versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit =
    signerName.trim() && signerEmail.trim() && hasSignature && confirmed && !submitting;

  // Render helpers

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 text-slate-500">
        <FileText className="w-12 h-12 text-slate-300" />
        <p className="text-lg font-medium">Dokument nicht gefunden</p>
        <p className="text-sm">Dieser Link ist ungültig oder abgelaufen.</p>
      </div>
    );
  }

  if (doc.status === 'signed' && !signed) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 text-center px-4">
        <CheckCircle className="w-16 h-16 text-emerald-500" />
        <h2 className="text-2xl font-bold text-slate-900">Bereits unterzeichnet</h2>
        <p className="text-slate-600">Dieses Dokument wurde bereits unterzeichnet.</p>
        {doc.signedAt && (
          <p className="text-sm text-slate-400">
            Unterzeichnet am{' '}
            {new Date(doc.signedAt).toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
            {doc.signedByName && ` von ${doc.signedByName}`}
          </p>
        )}
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 text-center px-4">
        <CheckCircle className="w-16 h-16 text-emerald-500" />
        <h2 className="text-2xl font-bold text-slate-900">Vielen Dank!</h2>
        <p className="text-slate-600 max-w-md">
          Das Dokument wurde erfolgreich unterzeichnet. Sie erhalten in Kürze eine Kopie per
          E-Mail.
        </p>
        {signedAt && (
          <p className="text-sm text-slate-400">
            Unterzeichnet am{' '}
            {signedAt.toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        )}
      </div>
    );
  }

  // Main view

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3 flex items-center">
        <div className="flex-1">
          <span className="text-xl font-bold tracking-tight text-slate-900">
            off<span style={{ color: '#f59e0b' }}>f</span>er
          </span>
        </div>
        <div className="flex-1 text-center">
          <span className="text-sm font-medium text-slate-700 truncate">{doc.title}</span>
        </div>
        <div className="flex-1" />
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Document content */}
        <Card className="bg-white shadow-sm border border-slate-200 rounded-xl">
          <CardContent className="p-8 md:p-12">
            <div
              dangerouslySetInnerHTML={{ __html: doc.content || doc.htmlContent || '' }}
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#1e293b',
              }}
              className="
                [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-slate-900
                [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-slate-800
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4
                [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
                [&_li]:mb-1
                [&_strong]:font-semibold
                [&_table]:w-full [&_table]:border-collapse [&_table]:mb-4
                [&_td]:border [&_td]:border-slate-200 [&_td]:px-3 [&_td]:py-2
                [&_th]:border [&_th]:border-slate-200 [&_th]:px-3 [&_th]:py-2 [&_th]:bg-slate-50 [&_th]:font-semibold
              "
            />
          </CardContent>
        </Card>

        {/* Signature section */}
        <Card className="bg-white shadow-sm border border-slate-200 rounded-xl">
          <CardContent className="p-8 md:p-12">
            <div className="flex items-center gap-2 mb-6">
              <PenLine className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-slate-900">Dokument unterzeichnen</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">
                  Ihr vollständiger Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Max Mustermann"
                  required
                  className="border-slate-200 focus:border-amber-400 focus:ring-amber-400"
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">
                  Ihre E-Mail-Adresse <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  placeholder="max@beispiel.de"
                  required
                  className="border-slate-200 focus:border-amber-400 focus:ring-amber-400"
                />
              </div>

              {/* Canvas signature pad */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Unterschrift <span className="text-red-500">*</span>
                </label>
                <div className="relative inline-block w-full max-w-[400px]">
                  <canvas
                    ref={canvasRef}
                    width={800}
                    height={300}
                    className="w-full h-[150px] border border-slate-200 rounded-lg bg-white cursor-crosshair touch-none block"
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={stopDraw}
                    onMouseLeave={stopDraw}
                    onTouchStart={startDraw}
                    onTouchMove={draw}
                    onTouchEnd={stopDraw}
                  />
                  {!hasSignature && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="text-slate-300 text-sm select-none">
                        Hier unterschreiben …
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSignature}
                  className="text-slate-500 hover:text-slate-700 text-xs px-0"
                >
                  Unterschrift löschen
                </Button>
              </div>

              {/* Confirmation checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-amber-500 shrink-0"
                />
                <span className="text-sm text-slate-600 leading-snug">
                  Ich bestätige, dass ich dieses Dokument gelesen habe und mit dem Inhalt
                  einverstanden bin.
                </span>
              </label>

              {/* Submit */}
              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full py-3 text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird unterzeichnet …
                  </span>
                ) : (
                  'Jetzt verbindlich unterzeichnen'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
